import React, { FC, useMemo } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import { classNames, format, ddayjs } from '@dt/fe-utils'
import { getExpireDay } from '@utils'
import themeMobx from '@mobx/app/theme.mobx'
import Content from './content'
import './index.scss'
import itemDisableImg from '@img/mkt/coupon-item-disable.png'
import itemImg from '@img/mkt/coupon-item.png'
import designateDisableImg from '@img/mkt/coupon-jian-disable.png'
import designateImg from '@img/mkt/coupon-jian.png'
import cashDisableImg from '@img/mkt/coupon-cash-disable.png'
import cashImg from '@img/mkt/coupon-cash.png'
import deliveryDisableImg from '@img/mkt/coupon-delivery-disable.png'
import deliveryImg from '@img/mkt/coupon-delivery.png'
import disableCouponImg from '@img/mkt/coupon-img-disable.png'
import couponImg from '@img/mkt/coupon-img.png'

interface IProps {
  type: 'get' | 'use';
  couponInfo: any;
  used?: boolean;
  expired?: boolean;
  selected?: boolean;
  unavailable?: boolean;
  unavailableReason?: string;
  cnt?: number;
  onClick?: (data: any) => void;
  onDetail?: (data: any) => void;
}

const imgMap: any = {
  cn: {
    item: {
      normal: itemImg,
      disable: itemDisableImg
    },
    cash: {
      normal: cashImg,
      disable: cashDisableImg
    },
    designate: {
      normal: designateImg,
      disable: designateDisableImg
    },
    order: {
      normal: designateImg,
      disable: designateDisableImg
    },
    ladder: {
      normal: designateImg,
      disable: designateDisableImg
    },
    express: {
      normal: deliveryImg,
      disable: deliveryDisableImg
    }
  },
  en: {
    item: {
      normal: couponImg,
      disable: disableCouponImg
    },
    cash: {
      normal: couponImg,
      disable: disableCouponImg
    },
    designate: {
      normal: couponImg,
      disable: disableCouponImg
    },
    order: {
      normal: couponImg,
      disable: disableCouponImg
    },
    ladder: {
      normal: couponImg,
      disable: disableCouponImg
    },
    express: {
      normal: couponImg,
      disable: disableCouponImg
    },
  }
}

const CouponItem: FC<IProps> = (props) => {
  const { type, couponInfo, unavailable, unavailableReason, used, expired, selected, cnt, onClick, onDetail } = props
  const isAddDate = useMemo(() => {
    return couponInfo?.lastChangeTimeInfo?.changeType === 'add'
  }, [couponInfo])
  const abnormalStatus: string = useMemo(() => {
    let status: string = ''
    if (used) {
      status = i18n.t('mkt.coupon.used')
    } else if (unavailableReason) {
      status = unavailableReason
    } else if (unavailable) {
      status = i18n.t('mkt.common.unavailable')
    } else if (expired) {
      status = i18n.t('mkt.coupon.expired')
    } else if (couponInfo?.status === 'disable') {
      status = i18n.t('mkt.giftCard.status_stopUsing')
    }
    return status
  }, [couponInfo, unavailable, unavailableReason, expired, used])
  return (
    <div
      data-cypid='mmgClickCoupon'
      className={`mkt-coupon-item ${abnormalStatus ? 'unavailable' : ''}`} onClick={() => { onClick && onClick(couponInfo) }}
    >
      <div className='coupon-item-area'>
        <div className={`coupon-mark ${couponInfo?.picture || abnormalStatus ? '' : couponInfo?.type}`} style={couponInfo?.picture && { backgroundImage: `Url(${couponInfo?.picture})` }}>
          {(selected === false || abnormalStatus) && <div className={`disable-mark ${themeMobx.data.backgroundMode === 'dark' ? 'dark' : ''}`}></div>}
          {!couponInfo?.picture &&
            <div className='coupon-type' style={{ backgroundImage: `Url(${imgMap[i18n.language === 'zh-CN' ? 'cn' : 'en'][couponInfo?.type || 'item'][abnormalStatus ? 'disable' : 'normal']})` }}>
            </div>}
        </div>
        <div className='coupon-info'>
          <div className='type'>{couponInfo?.couponName}</div>
          <Content type={`${couponInfo?.type} ${abnormalStatus ? 'used' : ''}`}>
            {couponInfo?.type === 'cash' && i18n.t('mkt.coupon.cashDeductionDetail', { fee: format.toPrice(couponInfo?.discountValue) })}
            {couponInfo?.type === 'item' &&
              <>
                {couponInfo?.discountType === 'discountFee' && i18n.t(`mkt.coupon.${couponInfo?.discountType}ItemDetail`, { fee: format.toPrice(couponInfo?.discountValue) })}
                {couponInfo?.discountType === 'discountRate' && i18n.t(`mkt.coupon.${couponInfo?.discountType}ItemDetail`, { fee: format.toDiscount('discountRate', couponInfo?.discountValue) })}
                {couponInfo?.discountType === 'specialOffer' && i18n.t(`mkt.coupon.${couponInfo?.discountType}ItemDetail`, { fee: format.toPrice(couponInfo?.discountValue) })}
              </>}
            {(couponInfo?.type === 'order' || couponInfo?.type === 'designate') &&
              <>
                {couponInfo?.discountType === 'discountFee' && i18n.t(`mkt.coupon.${couponInfo?.discountType}OrderDetail`, { orderFee: format.toPrice(couponInfo?.orderFee), discountFee: format.toPrice(couponInfo?.discountValue) })}
                {couponInfo?.discountType === 'discountRate' && i18n.t(`mkt.coupon.${couponInfo?.discountType}OrderDetail`, { orderFee: format.toPrice(couponInfo?.orderFee), discountFee: format.toDiscount('discountRate', couponInfo?.discountValue) })}
              </>}
            {
              couponInfo?.type === 'ladder' && i18n.t('mkt.coupon.discountFeeItem')
            }
            {couponInfo?.type === 'express' && i18n.t('mkt.coupon.expressItem')}
          </Content>
          {
            !used && (couponInfo?.status === 'enable' || couponInfo?.status === 'mutex') &&
              <div className='des-box'>
                {/* {
                  type === 'get' &&
                    <div className='des'>
                      {couponInfo?.whenEffect === 'today' && <span>{i18n.t('mkt.coupon.useType1')}（{couponInfo?.effectDay === null ? i18n.t('mkt.giftCard.useRuleExpired1') : i18n.t('mkt.coupon.validDays', { day: couponInfo?.effectDay })}）</span>}
                      {couponInfo?.whenEffect === 'tomorrow' && <span>{i18n.t('mkt.coupon.useType2')}（{couponInfo?.effectDay === null ? i18n.t('mkt.giftCard.useRuleExpired1') : i18n.t('mkt.coupon.validDays', { day: couponInfo?.effectDay })}）</span>}
                      {couponInfo?.whenEffect === 'specify' && <span>{format.toMonthDateYear(couponInfo?.startTime)} {i18n.t('mkt.common.to')} {couponInfo?.effectDay === null ? i18n.t('mkt.giftCard.useRuleExpired1') : format.toMonthDateYear(ddayjs(couponInfo?.startTime).add(couponInfo?.effectDay - 1, 'days').valueOf())}</span>}
                    </div>
                } */}
                {type === 'use' &&
                  <>
                    {
                      couponInfo?.endTime && getExpireDay(couponInfo?.endTime) > 0 && getExpireDay(couponInfo?.endTime) <= 3
                        ? <div className='des expire'>{i18n.t('mkt.coupon.expiresInSomeDays', { day: getExpireDay(couponInfo?.endTime) })}</div>
                        : <div className={classNames('des', { expire: isAddDate })}>{(couponInfo?.effectDay === null || couponInfo?.endTime >= '3000-01-01 00:00:00') ? i18n.t('mkt.giftCard.useRuleExpired1') : `${i18n.t(isAddDate ? 'mkt.common.toExtendDate' : 'mkt.common.toDate')} ${couponInfo?.endTime ? format.toMonthDateYear(ddayjs(couponInfo?.endTime).valueOf() - 1) : format.toMonthDateYear(ddayjs(couponInfo?.startTime).add(couponInfo?.effectDay - 1, 'days').valueOf())}`}</div>
                    }
                  </>}
                {
                  onDetail &&
                    <div className='detail'>
                      <span
                        data-cypid='mmgShowCouponDetail'
                        onClick={(e: any) => {
                          e.stopPropagation()
                          onDetail && onDetail(couponInfo)
                        }}
                      >{i18n.t('mkt.login.details')}
                      </span>
                      <i className='iconfont icon-information'></i>
                    </div>
                }
              </div>
          }
        </div>
        {selected === true && couponInfo?.status !== 'disable' &&
          <div className='select-mark has-select'>
            <i className='iconfont icon-checked'></i>
          </div>}
        {selected === false && couponInfo?.status !== 'disable' && <div className='select-mark no-select'></div>}
        {cnt && selected === undefined && couponInfo?.status !== 'disable' && <div className='cnt-mark'>x{cnt}</div>}
        {/* {
          used &&
            <div className='tip-area'>
              <span>{i18n.t('mkt.coupon.used')}</span>
            </div>
        } */}
        {/* {
          expired &&
            <div className='tip-area'>
              <span>{i18n.t('mkt.coupon.expired')}</span>
            </div>
        } */}
      </div>
      {
        abnormalStatus &&
          <div className='unavailable-area'>
            <div className='unavailable-info'>
              <span>{abnormalStatus}</span>
            </div>
          </div>
      }
    </div>
  )
}

export default observer(CouponItem)
